import CheckCircle from "@assets/images/check-circle.svg";
import ChevronDown from "@assets/images/chevron-down-2.svg";
import ChevronUp from "@assets/images/chevron-up-2.svg";
import InfoIcon from "@assets/images/info.svg";
import RefreshIcon from "@assets/images/refresh-primary.svg";
import ErrorIcon from "@assets/images/x-circle.svg";
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import Autocomplete from "@saleor/components/Autocomplete";
import DatePicker from "@saleor/components/DatePicker";
import FormSpacer from "@saleor/components/FormSpacer";
import MyTypography from "@saleor/components/MyTypography";
import { SingleSelectField } from "@saleor/components/SingleSelectField";
import { SingleTextField } from "@saleor/components/SingleTextField";
import {
  useExportSoldTosLazyQuery,
  useFilterUserManagementSaleGroupBySaleOrgQuery,
  useFilterUserManagementSaleOrgByBuQuery,
  useGetBusinessUnitQuery,
  useGetParentGroupsQuery,
  useGetPermissionGroupsQuery,
  useGetRoleListByGroupLazyQuery
} from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { StatusFilter } from "@saleor/userManagement/views/UserList/UserList";
import {
  mapEdgesToChoices,
  mapEdgesToChoicesAndCode,
  mapEdgesToItems,
  mapNodeToChoice
} from "@saleor/utils/maps";
import classNames from "classnames";
import { useFormik } from "formik";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import SVG from "react-inlinesvg";
import { FormattedMessage, useIntl } from "react-intl";

export interface UserListFilterProps {
  isOpen?: boolean;
  setFilter: (value) => void;
  statusFilter?: StatusFilter;
  dataSearchList?: any[];
}

const useStyles = makeStyles(
  theme => ({
    header: {
      cursor: "pointer",
      padding: "0.8rem 2.6rem",
      background: theme.palette.gardient.medium,
      colors: "white",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      height: "60px",
      "& .MuiTypography-root": {
        color: "white"
      },
      "& .MuiTypography-h6": {
        lineHeight: "2.4rem",
        fontSize: "18px"
      }
    },
    disableBtn: {
      "& svg path": {
        stroke: "#CCCCCC"
      }
    },
    searchBox: {
      "& .MuiFormControl-root": {
        height: "36px"
      },
      margin: "2.4rem 0"
    },
    wrapper: {
      borderRadius: 6,
      background: "white",
      boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
      overflow: "hidden",
      marginTop: "1.6rem",
      marginBottom: "1.6rem",
      width: "100%"
    },
    selectField: {
      height: "36px",
      "& .MuiSelect-root": {
        padding: "0.8rem 1.2rem",
        color: theme.palette.grey[800]
      }
    },
    leftSide: {
      width: "100%",
      margin: 0,
      borderRight: `1px dashed ${theme.palette.grey[500]}`
    },
    rightSide: {
      width: "100%",
      margin: 0
    },
    flex: {
      display: "flex"
    },
    radioGroup: {
      flexDirection: "row"
    },
    container: {},
    divider: {
      padding: "0 2.4rem"
    },
    bottomBar: {
      padding: "2.4rem 2.4rem 2.4rem 0",
      display: "flex",
      justifyContent: "space-between"
    },
    actionButton: {
      width: "132px",
      fontSize: "14px",
      height: "40px"
    },
    icon: {
      marginRight: "1.1rem"
    },
    info: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center"
    },
    colorInfo: {
      color: theme.palette.grey[700]
    },
    colorSuccess: {
      color: theme.palette.success.main
    },
    colorError: {
      color: theme.palette.error.light
    }
  }),
  { name: "UserListFilter" }
);

enum UserStatus {
  all = "all",
  active = "1",
  inactive = "0"
}

enum DateType {
  create = "create",
  update = "update",
  login = "login"
}

const ALL = {
  label: "All",
  value: "all"
};

export interface FormikInitiaValues {
  employeeId: string;
  firstName: string;
  lastName: string;
  userGroup: string;
  role: string;
  email: string;
  updateBy: string;
  status: string;
  dateType: string;
  startDate: string | null;
  endDate: string | null;
  soldTo: string;
  bu: string;
  saleOrganization: string;
  saleGroup: string;
}

const UserListFilter: React.FC<UserListFilterProps> = ({
  setFilter,
  statusFilter,
  dataSearchList
}) => {
  // const { isOpen = true } = props;
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [reset, setReset] = useState<boolean>(true);
  const [isFormHasChange, setIsFormHasChange] = useState<boolean>(true);
  const classes = useStyles();

  const formik = useFormik<FormikInitiaValues>({
    initialValues: {
      employeeId: "",
      firstName: "",
      lastName: "",
      userGroup: "",
      role: "",
      email: "",
      updateBy: "",
      status: "",
      dateType: "",
      startDate: null,
      endDate: null,
      soldTo: "",
      bu: "",
      saleOrganization: "",
      saleGroup: ""
    },
    onSubmit: () => {
      //
    }
  });

  const optionsRadio = [
    {
      label: intl.formatMessage({ defaultMessage: "All" }),
      value: UserStatus.all
    },
    {
      label: intl.formatMessage({ defaultMessage: "Active" }),
      value: UserStatus.active
    },
    {
      label: intl.formatMessage({ defaultMessage: "Inactive" }),
      value: UserStatus.inactive
    }
  ];

  const optionDateTypeChoices = [
    {
      label: intl.formatMessage({ defaultMessage: "Create Date" }),
      value: DateType.create
    },
    {
      label: intl.formatMessage({ defaultMessage: "Last Update Date" }),
      value: DateType.update
    },
    {
      label: intl.formatMessage({ defaultMessage: "Last Login Date" }),
      value: DateType.login
    }
  ];

  const { data: dataBuChoice } = useGetBusinessUnitQuery({
    displayLoader: true
  });

  const businessUnitChoices = useMemo(
    () => mapEdgesToChoices(dataBuChoice?.businessUnits),
    [dataBuChoice]
  );

  const {
    data: dataSaleOrganization
  } = useFilterUserManagementSaleOrgByBuQuery({
    displayLoader: true,
    variables: {
      filter: {
        businessUnit: formik.values.bu
          ? [
              (businessUnitChoices || [])?.find(
                u => u.value === formik.values.bu
              )?.label
            ]
          : []
      },
      first: 100
    }
  });

  const saleOrganizationChoices = useMemo(
    () =>
      mapEdgesToChoicesAndCode(
        dataSaleOrganization?.filterUserManagementSaleOrgByBu
      ),
    [dataSaleOrganization]
  );

  const {
    data: dataSaleGroup
  } = useFilterUserManagementSaleGroupBySaleOrgQuery({
    displayLoader: true,
    variables: {
      filter: {
        salesOrganization: formik.values.saleOrganization
          ? [
              (
                mapEdgesToItems(
                  dataSaleOrganization.filterUserManagementSaleOrgByBu
                ) || []
              )?.find(u => u.id === formik.values.saleOrganization)?.code
            ]
          : []
      },
      first: 1000
    }
  });

  const { data: dataUserGroup } = useGetParentGroupsQuery({
    displayLoader: true
  });

  const { data: dataAllRoles } = useGetPermissionGroupsQuery({
    displayLoader: true
  });

  const [
    getRoleListByGroup,
    { data: dataRoleList }
  ] = useGetRoleListByGroupLazyQuery();

  const [
    getDataSuggestions,
    { data: fetchDataSuggestions, loading: loadingDataSuggestion }
  ] = useExportSoldTosLazyQuery();

  const saleGroupChoices = useMemo(
    () =>
      dataSaleGroup?.filterUserManagementSaleGroupBySaleOrg?.edges?.map(
        ({ node }) => ({
          label: node.displayText,
          value: node.id
        })
      ) || [],
    [dataSaleGroup]
  );

  const allRoleChoices = useMemo(() => {
    const arr = mapEdgesToChoices(dataAllRoles?.permissionGroups);
    if (arr.length > 0) {
      arr.unshift(ALL);
    }
    return arr;
  }, [dataAllRoles]);

  const userGroupChoices = useMemo(() => {
    const arr = mapEdgesToChoices(dataUserGroup?.parentGroups);
    if (arr.length > 1) {
      arr.unshift(ALL);
    }
    return arr;
  }, [dataUserGroup]);

  const roleChoices = useMemo(() => {
    if (formik.values.userGroup === "") {
      return [];
    }
    if (formik.values.userGroup === ALL.value) {
      return allRoleChoices || [];
    }
    const arr = mapNodeToChoice(dataRoleList?.parentGroup?.groups);
    if (arr.length > 0) {
      arr.unshift(ALL);
    }
    return arr;
  }, [dataRoleList, formik.values.userGroup, dataAllRoles]);

  const handleOnChangeGroup = e => {
    formik.handleChange(e);
    formik.setFieldValue("role", "");
    if (e.target.value === ALL.value) {
      return;
    }
    getRoleListByGroup({
      variables: {
        id: e.target.value
      }
    });
  };

  const handleResetForm = () => {
    formik.resetForm();
    setReset(prev => !prev);
    setFilter({});
  };

  const handleDate = e => {
    formik.handleChange(e);
  };

  const handleSearchUser = () => {
    const data: Record<string, any> = {};
    data.employeeId = formik.values.employeeId || undefined;
    data.lastName = formik.values.lastName || undefined;
    data.firstName = formik.values.firstName || undefined;
    data.email = formik.values.email || undefined;
    data.soldTo = formik.values.soldTo || undefined;
    data.updateBy = formik.values.updateBy || undefined;
    data.status = formik.values.status || UserStatus.all;

    if (formik.values.status && formik.values.status !== UserStatus.all) {
      data.status = formik.values.status === UserStatus.active;
    }
    if (data.status === UserStatus.all) {
      data.status = undefined;
    }
    if (formik.values.userGroup && formik.values.userGroup !== ALL.value) {
      data.parentGroup = formik.values.userGroup;
    }
    if (formik.values.role && formik.values.role !== ALL.value) {
      data.group = formik.values.role;
    }
    const dateRange = {
      gte: formik.values.startDate,
      lte: formik.values.endDate
    };
    if (formik.values.dateType === DateType.login) {
      data.lastLoginDate = dateRange;
    }
    if (formik.values.dateType === DateType.create) {
      data.createDate = dateRange;
    }
    if (formik.values.dateType === DateType.update) {
      data.lastUpdateDate = dateRange;
    }
    if (formik.values.bu) {
      data.bu = formik.values.bu;
    }
    if (formik.values.saleOrganization) {
      data.saleOrg = Number(formik.values.saleOrganization);
    }
    if (formik.values.saleGroup) {
      data.saleGroup = Number(formik.values.saleGroup);
    }
    setFilter({ ...data });
  };

  const dataOptions = useMemo(() => {
    const data = mapEdgesToItems(fetchDataSuggestions?.exportSoldTos) || [];
    return data.map(data => ({
      title: data.displayText,
      value: data.id
    }));
  }, [fetchDataSuggestions?.exportSoldTos]);
  useEffect(() => {
    if (!formik.dirty) {
      setIsFormHasChange(true);
      return;
    }
    setIsFormHasChange(false);
  }, [formik.values]);

  useEffect(() => {
    if (formik.values.soldTo.length >= 2) {
      getDataSuggestions({
        variables: {
          first: 100,
          filter: {
            search: formik.values.soldTo
          }
        }
      });
    }
  }, [formik.values.soldTo]);
  useEffect(() => {
    if (dataSearchList?.length) {
      setIsOpen(false);
    }
  }, [dataSearchList]);

  const renderMessase = () => {
    let srcImg = null;
    let message = "";
    let classNameMessage = "";
    switch (statusFilter) {
      case StatusFilter.INFO:
        srcImg = InfoIcon;
        message = intl.formatMessage({
          defaultMessage: "กรุณากรอกข้อมูลและกดค้นหา"
        });
        classNameMessage = classes.colorInfo;
        break;
      case StatusFilter.SUCCESS:
        srcImg = CheckCircle;
        message = intl.formatMessage({
          defaultMessage: "พบรายการที่เกี่ยวข้อง"
        });
        classNameMessage = classes.colorSuccess;
        break;
      case StatusFilter.ERROR:
        srcImg = ErrorIcon;
        message = intl.formatMessage({
          defaultMessage: "ไม่พบรายการที่เกี่ยวข้อง"
        });
        classNameMessage = classes.colorError;
        break;
      default:
        srcImg = InfoIcon;
        message = intl.formatMessage({
          defaultMessage: "กรุณากรอกข้อมูลและกดค้นหา"
        });
        break;
    }

    return (
      <Box className={classes.info} data-testid="info-message">
        <img width={20} src={srcImg} alt="info" className={classes.icon} />
        <MyTypography
          variant="body3"
          align={"center"}
          className={classNameMessage}
        >
          {message}
        </MyTypography>
      </Box>
    );
  };

  return (
    <>
      <Box className={classes.wrapper} data-testid="userListFilter">
        <Grid
          className={classes.header}
          container
          data-testid="userListFilter-collapseButton"
          onClick={() => setIsOpen(!isOpen)}
        >
          <MyTypography variant="h6">
            <FormattedMessage defaultMessage="ค้นหาผู้ใช้งาน" />
          </MyTypography>
          {isOpen ? <SVG src={ChevronUp} /> : <SVG src={ChevronDown} />}
        </Grid>
        <Collapse
          data-testid="userListFilter-collapseForm"
          data-test-in={isOpen}
          in={isOpen}
          className={classes.container}
        >
          <Grid className={classes.searchBox} container>
            <Grid
              container
              item
              xs={9}
              spacing={5}
              className={classes.leftSide}
            >
              <Grid item xs={4}>
                <MyTypography variant={"subtitle3"}>
                  {intl.formatMessage({
                    defaultMessage: "Existing User ID / Exployee ID"
                  })}
                </MyTypography>
                <SingleTextField
                  name={"employeeId"}
                  id="employeeId"
                  value={formik.values.employeeId}
                  onChange={formik.handleChange}
                  placeholder={"ระบุ Existing User ID"}
                />
              </Grid>
              <Grid item xs={4}>
                <MyTypography variant={"subtitle3"}>
                  {intl.formatMessage({
                    defaultMessage: "User Group"
                  })}
                </MyTypography>
                <SingleSelectField
                  className={classes.selectField}
                  menuListCustomStyle={{
                    overflowY: "auto"
                  }}
                  placeholder={"เลือก User Group"}
                  choices={userGroupChoices}
                  name={"userGroup"}
                  id="userGroup"
                  testId="userGroup"
                  InputProps={{
                    inputProps: {
                      "data-testid": "input-userGroup"
                    }
                  }}
                  value={formik.values.userGroup}
                  onChange={handleOnChangeGroup}
                />
              </Grid>
              <Grid item xs={4}>
                <MyTypography variant={"subtitle3"}>
                  {intl.formatMessage({
                    defaultMessage: "Role"
                  })}
                </MyTypography>
                <SingleSelectField
                  className={classes.selectField}
                  menuListCustomStyle={{
                    overflowY: "auto"
                  }}
                  labelEmpty={
                    <MyTypography variant={"body3"}>
                      Please select the <b>user group</b> field, before.
                    </MyTypography>
                  }
                  name={"role"}
                  id="role"
                  testId="role"
                  InputProps={{
                    inputProps: {
                      "data-testid": "input-role"
                    }
                  }}
                  value={formik.values.role}
                  onChange={formik.handleChange}
                  placeholder={"เลือก Roles"}
                  choices={roleChoices}
                  disabled={formik.values.userGroup === ALL.value}
                />
              </Grid>
              <Grid item xs={4}>
                <MyTypography variant={"subtitle3"}>
                  {intl.formatMessage({
                    defaultMessage: "First Name"
                  })}
                </MyTypography>
                <SingleTextField
                  name={"firstName"}
                  id="firstName"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  placeholder={"ระบุ First Name"}
                />
              </Grid>
              <Grid item xs={4}>
                <MyTypography variant={"subtitle3"}>
                  {intl.formatMessage({
                    defaultMessage: "Last Name"
                  })}
                </MyTypography>
                <SingleTextField
                  name={"lastName"}
                  id="lastName"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  placeholder={"ระบุ Last Name"}
                />
              </Grid>
              <Grid item xs={4}>
                <MyTypography variant={"subtitle3"}>
                  {intl.formatMessage({
                    defaultMessage: "Email"
                  })}
                </MyTypography>
                <SingleTextField
                  name={"email"}
                  id="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  placeholder={"ระบุ Email"}
                />
              </Grid>
              <Grid item xs={4}>
                <MyTypography variant={"subtitle3"}>
                  {intl.formatMessage({
                    defaultMessage: "Update By"
                  })}
                </MyTypography>
                <SingleTextField
                  onChange={formik.handleChange}
                  placeholder={"ระบุ Update By"}
                  name={"updateBy"}
                  id="updateBy"
                  value={formik.values.updateBy}
                />
              </Grid>
              <Grid item xs={8}>
                <MyTypography variant={"subtitle3"}>
                  {intl.formatMessage({
                    defaultMessage: "Status"
                  })}
                </MyTypography>
                <RadioGroup
                  className={classes.radioGroup}
                  aria-labelledby="user-list-filfer-radio-buttons-group"
                  name={"status"}
                  id="status"
                  data-testid="status"
                  value={formik.values.status || UserStatus.all}
                  data-test-value={formik.values.status || UserStatus.all}
                  onChange={formik.handleChange}
                >
                  {optionsRadio.map((o, oKey) => (
                    <FormControlLabel
                      key={`radio${oKey}`}
                      id={`radio${oKey}`}
                      data-testid="status-option"
                      value={o.value}
                      control={<Radio />}
                      label={
                        <MyTypography variant={"body3"}>{o.label}</MyTypography>
                      }
                    />
                  ))}
                </RadioGroup>
              </Grid>
              <Grid item xs={4}>
                <MyTypography variant={"subtitle3"}>
                  {intl.formatMessage({
                    defaultMessage: "Date Type"
                  })}
                </MyTypography>
                <SingleSelectField
                  className={classes.selectField}
                  menuListCustomStyle={{
                    overflowY: "auto"
                  }}
                  name={"dateType"}
                  id="dateType"
                  testId="dateType"
                  InputProps={{
                    inputProps: {
                      "data-testid": "input-dateType"
                    }
                  }}
                  onChange={formik.handleChange}
                  value={formik.values.dateType}
                  placeholder={"เลือก Date (From - To)"}
                  choices={optionDateTypeChoices}
                />
              </Grid>
              <Grid item xs={4} data-testid="startDate-wrapper">
                <MyTypography variant={"subtitle3"}>
                  {intl.formatMessage({
                    defaultMessage: "Start Date"
                  })}
                </MyTypography>
                <DatePicker
                  placeholder={"เลือกวันเริ่มต้น"}
                  name={"startDate"}
                  id="startDate"
                  value={formik.values.startDate}
                  onChange={handleDate}
                  maxDate={
                    formik.values.endDate
                      ? moment(formik.values.endDate)
                      : undefined
                  }
                  minDate={
                    formik.values.endDate
                      ? moment(formik.values.endDate).subtract(1, "y")
                      : undefined
                  }
                  disableManualInput
                />
              </Grid>
              <Grid item xs={4} data-testid="endDate-wrapper">
                <MyTypography variant={"subtitle3"}>
                  {intl.formatMessage({
                    defaultMessage: "End Date"
                  })}
                </MyTypography>
                <DatePicker
                  placeholder={"เลือกวันสิ้นสุด"}
                  name={"endDate"}
                  id="endDate"
                  value={formik.values.endDate}
                  onChange={handleDate}
                  maxDate={
                    formik.values.startDate
                      ? moment(formik.values.startDate).add(1, "y")
                      : undefined
                  }
                  minDate={undefined}
                  disableManualInput
                />
              </Grid>
            </Grid>
            <Grid
              container
              item
              xs={3}
              spacing={5}
              className={classes.rightSide}
            >
              <Grid item xs={12}>
                <div className={classes.flex}>
                  <MyTypography variant={"subtitle3"}>Sold to</MyTypography>
                  &nbsp;(
                  <MyTypography variant={"body3"}>
                    {intl.formatMessage({
                      defaultMessage: "External"
                    })}
                  </MyTypography>
                  )
                </div>
                <Autocomplete
                  resetAlways
                  optionsProp={dataOptions}
                  placeholderProps={intl.formatMessage({
                    defaultMessage: "ค้นหา Sold to...",
                    description: "label"
                  })}
                  onChangeProps={value => {
                    if (value) {
                      formik.setFieldValue("soldTo", value);
                    }
                  }}
                  loading={loadingDataSuggestion}
                  onSearch={value => formik.setFieldValue("soldTo", value)}
                  isReset={reset}
                  id="soldTo"
                  name="soldTo"
                  data-testid="soldTo"
                />
              </Grid>
              <Grid item xs={12}>
                <div className={classes.flex}>
                  <MyTypography variant={"subtitle3"}>BU</MyTypography>
                  &nbsp;(
                  <MyTypography variant={"body3"}>
                    {intl.formatMessage({
                      defaultMessage: "Internal"
                    })}
                  </MyTypography>
                  )
                </div>
                <SingleSelectField
                  className={classes.selectField}
                  menuListCustomStyle={{
                    overflowY: "auto"
                  }}
                  name={"bu"}
                  id="bu"
                  testId="bu"
                  InputProps={{
                    inputProps: {
                      "data-testid": "input-bu"
                    }
                  }}
                  value={formik.values.bu}
                  onChange={data => {
                    formik.handleChange(data);
                    formik.setFieldValue("saleGroup", "");
                    formik.setFieldValue("saleOrganization", "");
                  }}
                  placeholder={"เลือก BU"}
                  choices={businessUnitChoices}
                />
              </Grid>
              <Grid item xs={12}>
                <div className={classes.flex}>
                  <MyTypography variant={"subtitle3"}>
                    Sale Organization
                  </MyTypography>
                  &nbsp;(
                  <MyTypography variant={"body3"}>
                    {intl.formatMessage({
                      defaultMessage: "Internal"
                    })}
                  </MyTypography>
                  )
                </div>
                <SingleSelectField
                  className={classes.selectField}
                  menuListCustomStyle={{
                    overflowY: "auto"
                  }}
                  disabled={!formik.values.bu}
                  name={"saleOrganization"}
                  id="saleOrganization"
                  testId="saleOrganization"
                  InputProps={{
                    inputProps: {
                      "data-testid": "input-saleOrganization"
                    }
                  }}
                  value={formik.values.saleOrganization}
                  onChange={data => {
                    formik.handleChange(data);
                    formik.setFieldValue("saleGroup", "");
                  }}
                  placeholder={"เลือก Sale Organization"}
                  choices={saleOrganizationChoices}
                />
              </Grid>
              <Grid item xs={12}>
                <div className={classes.flex}>
                  <MyTypography variant={"subtitle3"}>Sale Group</MyTypography>
                  &nbsp;(
                  <MyTypography variant={"body3"}>
                    {intl.formatMessage({
                      defaultMessage: "Internal"
                    })}
                  </MyTypography>
                  )
                </div>
                <SingleSelectField
                  className={classes.selectField}
                  menuListCustomStyle={{
                    overflowY: "auto"
                  }}
                  name={"saleGroup"}
                  id="saleGroup"
                  testId="saleGroup"
                  InputProps={{
                    inputProps: {
                      "data-testid": "input-saleGroup"
                    }
                  }}
                  disabled={
                    !formik.values.bu || !formik.values.saleOrganization
                  }
                  value={formik.values.saleGroup}
                  onChange={formik.handleChange}
                  placeholder={"เลือก Sale Group"}
                  choices={saleGroupChoices}
                />
              </Grid>
            </Grid>
          </Grid>

          <Box className={classes.divider}>
            <FormSpacer />
            <Divider />
          </Box>

          <Box className={classes.bottomBar} ml={3}>
            {renderMessase()}
            <Box className={classes.flex}>
              <Box mr={2}>
                <Button
                  disabled={isFormHasChange}
                  className={classNames(
                    classes.actionButton,
                    isFormHasChange && classes.disableBtn
                  )}
                  variant={"outlined"}
                  color={"secondary"}
                  onClick={handleResetForm}
                  id="resetButton"
                  name="resetButton"
                  data-testid="resetButton"
                >
                  <SVG src={RefreshIcon} width={24} height={24} />
                  <FormattedMessage defaultMessage="ล้างข้อมูล" />
                </Button>
              </Box>
              <Box>
                <Button
                  disabled={isFormHasChange}
                  className={classes.actionButton}
                  variant={"contained"}
                  color={"secondary"}
                  onClick={handleSearchUser}
                  id="searchButton"
                  name="searchButton"
                  data-testid="searchButton"
                >
                  <SearchIcon style={{ fontSize: "2.4rem" }} />
                  <FormattedMessage defaultMessage="ค้นหา" />
                </Button>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </>
  );
};

export default UserListFilter;
